import type AccountStore from "@stores/AccountStore";
import {
  ACTIVE_ACCOUNT_STORAGE_KEY,
  IMPERSONATE_STORAGE_KEY,
  setSessionExpiredHandler,
} from "@src/utils/apiAuthInterceptor";
import { track, EVENTS } from "@src/utils/analytics";

// Read and cleared by LoginScreen, so the user lands on a page that explains
// why they're there instead of a bare login form. sessionStorage survives the
// hard navigation below but not the tab, which is exactly the lifetime wanted.
export const SESSION_EXPIRED_STORAGE_KEY = "gg.sessionExpired";

// FarmStore's own key. Duplicated rather than imported to keep this module out
// of the store import graph (it runs before React mounts).
const CURRENT_FARM_STORAGE_KEY = "gg.currentFarmId";

function forget(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Wire the interceptor's 401 signal to a real sign-out.
 *
 * The app decides "am I logged in?" from a MobX flag set once at boot, so
 * before this existed a session that died server-side left the tab polling
 * forever: empty screens, a toast per failed request, and no way back to the
 * login form short of a manual reload.
 *
 * Reuses AccountStore.logout(), which resets analytics, calls signOut() — that
 * drops the stale cookie, which is also what stops the backend re-checking a
 * dead token on every poll — and then does a FULL page navigation. The hard
 * navigation matters: several pollers re-arm through recursive setTimeout and
 * are not visibility-aware, so a soft route change would leave them running.
 */
export function installSessionExpiredHandler(accountStore: AccountStore): void {
  setSessionExpiredHandler((url) => {
    // Before logout() resets the analytics identity, so the event stays tied
    // to the person. Path only — query strings can carry ids.
    let path = url;
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch {
      /* keep the raw string */
    }
    track(EVENTS.SESSION_LOST, { reason: "401", path, page: window.location.pathname });

    // Per-tab / per-browser context that logout() leaves behind. Left in place
    // it would ride along into the next session as a stale X-Active-Account-Id
    // or X-Impersonate-User-Id header.
    forget(window.localStorage, ACTIVE_ACCOUNT_STORAGE_KEY);
    forget(window.localStorage, CURRENT_FARM_STORAGE_KEY);
    forget(window.sessionStorage, IMPERSONATE_STORAGE_KEY);

    try {
      window.sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, "1");
    } catch {
      /* ignore — the redirect still happens, just without the notice */
    }

    void accountStore.logout();
  });
}

/** One-shot read: true if this page load followed an expired session. */
export function consumeSessionExpiredNotice(): boolean {
  try {
    if (window.sessionStorage.getItem(SESSION_EXPIRED_STORAGE_KEY) !== "1") {
      return false;
    }
    window.sessionStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}
